/**
 * 提示词工具 UI 文案字典（官方 locale 命名空间 `prompt-tool`）。
 *
 * 约定：
 * - 只放用户可见文案；provider/model id、文件路径、用户内容、协议 code 不进字典。
 * - zh 是唯一事实源：键类型由它派生，en 用 Record<Key, string> 强制键集一致
 *   （缺键/多键都是编译错误，等价于官方类型化 register 的运行时校验）。
 * - 渲染时经 {@link Translate} 求值（调用时读当前语言），不在模块顶层缓存译文。
 */
import type { Translate } from '@deepseek-ai/dsh-client-ui-slots'
import { PARAMS_EN, PARAMS_ZH } from './locales-params.ts'
import { PROMPTS_EN, PROMPTS_ZH } from './locales-prompts.ts'
import { CARDS_EN, CARDS_ZH } from './locales-cards.ts'
import { TRIGGERS_EN, TRIGGERS_ZH } from './locales-triggers.ts'

/** 官方 locale 服务注册用的命名空间。 */
export const PROMPT_TOOL_NS = 'prompt-tool'

const ZH_CORE = {
  'app.title': '提示词工具',
  'app.backToChat': '返回对话',
  'app.loading': '读取中',
  'app.statusSummary': '{configs} 配置 · {enabled} 启用',
  'app.open': '打开提示词工具',
  'app.close': '关闭提示词工具',
  'app.panelAria': '提示词工具',
  'nav.aria': '提示词工具页面',
  'tab.label': '提示词工具',

  'settings.aria': '提示词工具基础设置',
  'settings.saveRejected': '设置未保存，请检查当前权限或重新读取后重试。',
  'settings.writePreset.label': '启用提示词模块',
  'settings.writePreset.hint': '关闭后停止模块注入并撤回工具贡献，模块定义与生成文件保留。',
  'settings.presetTemplate.title': '模块模板',
  'settings.presetTemplate.hint': '新会话默认挂载的模块；在官方界面给空白会话切换模块时，工作台会自动跟随该模块。完整模块管理与提示词配置请点左上角的悬浮按钮（可拖动）打开「提示词工具」工作台。',
  'settings.switched': '已切换到 {id}，当前空会话已重组。',
  'settings.switchDefaultOnly': '默认模块已更新为 {id}；当前会话未切换：{reason}',
  'settings.switchPending': '默认模块已更新为 {id}；当前没有可重组的空会话。',
  'settings.switchFailed': '切换模块失败：{reason}',
  'settings.switchReason.sessionNotBlank': '当前会话已有内容，官方只允许空会话切换；本次只更新后续会话默认模块',


  'page.features.label': '主会话',
  'page.subagent.label': '子代理',
  'page.tools.label': '工具预览',
  'page.skills.label': '技能设置',
  'page.modules.label': '模块',

  'main.aria': '主会话与全局',
  'main.addTemplate': '添加模板 · {layer}',
  'main.addToolTemplate': '添加工具模板…',
  'main.addVariables': '添加模板变量',
  'main.newBlankTool': '新建空白工具',
  'subagent.aria': '子代理',
  'configList.create': '新建',
  'configList.emptyPreStep': '当前模块模板消息批层无配置；可新建自定义配置（写入激活模块 module.yml，随模块走）。',

  'tools.aria': '工具预览',
  'tools.search.aria': '搜索工具',
  'tools.search.placeholder': '搜索工具名或描述',
  'tools.surface.session': '当前会话工具',
  'tools.surface.preset': '官方预设工具能力',
  'tools.surface.source.aria': '官方预设来源',
  'tools.surface.source.placeholder': '选择官方预设',
  'tools.loadingPresets': '正在读取官方预设…',
  'tools.loadFailed': '官方预设列表读取失败',
  'tools.noPresets': '暂无可用官方预设。',
  'tools.surface.sourceLabel': '工具面',
  'tools.surface.count': '显示 {visible} / {total} 个工具',
  'tools.surface.emptySource': '该来源暂无可见工具。',
  'tools.surface.emptyFilter': '无匹配工具，请调整搜索。',
  'tools.surface.list.aria': '模型可见工具',
  'tools.surface.card.aria': '查看工具 {name}',
  'tools.surface.badge.visible': '模型可见',
  'tools.surface.detail.name': '完整名称',
  'tools.surface.detail.from': '来自',
  'tools.surface.detail.status': '可见状态',
  'tools.surface.detail.description': '工具描述',
  'tools.surface.detail.noDescription': '（无描述）',
  'tools.surface.sub.count': '{count} 个',
  'tools.surface.noSession': '尚未选择当前会话；不会自动创建或恢复会话。',
  'tools.surface.noPreset': '请选择模块后读取工具能力。',
  'tools.surface.origin': '来源：',
  'tools.surface.refresh': '刷新工具',
  'tools.surface.loading': '正在读取工具…',
  'tools.surface.readFailed': '工具面读取失败',
  'tools.surface.retrySuffix': '；可刷新重试。',
  'tools.surface.note.session': '刷新不升级既有会话的 generation，也不会自动 resume 会话。',
  'tools.surface.note.preset': '模块能力不代表当前会话；修改模块不替换既有会话的冻结 generation。',

  'characters.aria': '角色管理',
  'characters.importing': '导入中…',
  'characters.badge.imported': '已导入当前模块',
  'characters.remove': '从当前模块移除',
  'characters.removing': '移除中…',
  'characters.apply': '导入到当前模块',
  'characters.openDir.label': '打开角色卡目录',
  'characters.openDir.aria': '打开角色卡目录：{name}',
  'characters.delete.label': '删除角色卡',
  'characters.delete.hintImported': '已导入当前模块：先「从当前模块移除」，再删除角色卡',
  'characters.delete.aria': '删除角色卡：{name}',
  'characters.confirmDelete': '确认删除',
  'characters.cancel': '取消',
  'characters.notice.applied': '已导入到当前模块（{count} 条配置）',
  'characters.notice.applyFailed': '导入到当前模块失败：{reason}',
  'characters.notice.removed': '已从当前模块移除（{count} 条配置）',
  'characters.notice.removeFailed': '移除失败：{reason}',
  'characters.notice.deleted': '角色卡 {id} 已删除，它在当前模块里的条目一并移除',
  'characters.notice.deleteFailed': '删除失败：{reason}',
  'characters.notice.opened': '已打开角色卡目录：{path}',
  'characters.notice.openFailed': '打开目录失败：{reason}',

  'skills.aria': '技能设置',
  'skills.tabs.aria': '技能状态筛选',
  'skills.tabs.all': '全部',
  'skills.tabs.model': '模型可用',
  'skills.tabs.user': '用户可用',
  'skills.tabs.blocked': '两端关闭',
  'skills.incomplete': '当前会话的技能发现尚未完成，注册状态未确认；文件调用策略仍可按权限修改。',
  'skills.refresh': '刷新',
  'skills.library.title': '技能目录与资产',
  'skills.library.meta': '{count} 个技能 · 来自官方各类技能根',
  'skills.library.hint': '技能实体留在各自的技能根里，本插件不搬迁、不建链接；停用/恢复只改写该技能 SKILL.md 的调用策略键。',
  'skills.library.unknown': '（用户技能目录未知，请重新读取配置）',
  'skills.library.open': '打开用户技能目录',
  'skills.library.footnote': '停用 = 改写该技能 SKILL.md 的官方两个键（disable-model-invocation / user-invocable）：模型目录、skill 工具与斜杠命令各自独立开关；正文、注释与其余字段逐字保留，写回 false/true 即恢复。只读来源（如官方内置包目录）写入失败会如实报错。',
  'skills.dir.rescan': '重扫',
  'skills.dir.cancel': '取消',
  'skills.import.pick': '导入技能',
  'skills.import.pick.hint': '选择宿主机目录并把它的内容复制到用户技能目录（复制，不是引用）',
  'skills.folders.pick': '引用文件夹',
  'skills.folders.remove': '移除引用',
  'skills.folders.empty': '还没有引用任何技能文件夹。',
  'skills.folders.duplicate': '该文件夹已在引用列表里',
  'skills.folders.hint': '引用只登记路径，不复制文件；移除引用不会删除源文件。引用技能的开关写入原文件，删除操作会移动对应源文件到回收站。',
  'skills.source.aria': '按来源筛选技能',
  'skills.source.all': '全部来源',
  'skills.source.project-dsh': '项目 .dsh/skills',
  'skills.source.project-agents': '项目 .agents/skills',
  'skills.source.custom': '技能文件夹',
  'skills.source.user-dsh': '用户技能目录',
  'skills.source.user-agents': '用户 .agents/skills',
  'skills.source.bundled': '官方内置',
  'skills.source.other': '其他提供者',
  'skills.create.open': '创建技能',
  'skills.create.close': '收起创建表单',
  'skills.create.name': '技能名（kebab-case）',
  'skills.create.namePlaceholder': '例如 my-skill',
  'skills.create.description': '描述',
  'skills.create.descriptionPlaceholder': '模型据此判断何时使用该技能',
  'skills.create.content': '正文（Markdown）',
  'skills.create.contentPlaceholder': '# 使用说明…',
  'skills.create.submit': '创建',
  'skills.create.invalid': '技能名须为 kebab-case 且描述必填',
  'skills.create.hint': '创建即写入用户技能目录，官方提供者立刻能发现。',
  'skills.delete.title': '删除技能 {name}？',
  'skills.delete.description': '将删除此路径对应的技能并移入回收站，可人工恢复：{path}。引用目录里的技能会直接移动源文件；仅移除文件夹引用不会删除文件。',
  'skills.delete.confirm': '删除',
  'skills.delete.failed': '删除失败，请查看错误提示后重试。',
  'skills.filter.aria': '过滤技能列表',
  'skills.filter.placeholder': '过滤技能：名称 / 目录 / 描述…',
  'skills.group.meta': '{count} 个技能 · 优先级 {rank}',
  'skills.empty.title': '没有发现任何技能',
  'skills.empty.hint': '展开上方目录与资产，选择「导入技能」复制技能，或选择「引用文件夹」使用已有目录；也可以创建新技能。',
  'skills.noMatch': '没有匹配当前筛选的技能。',
  'skills.notice.dirPickFailed': '选择目录失败：{reason}',
  'skills.notice.imported': '已复制 {count} 个技能文件到 {path}',
  'skills.notice.importedOverwrite': '已复制 {count} 个技能文件到 {path}，覆盖 {overwritten} 个已确认的同名技能',
  'skills.overwrite.title': '覆盖同名技能？',
  'skills.overwrite.description': '以下技能目录已存在：{names}。确认后替换目录内容，不保留历史版本。',
  'skills.overwrite.confirm': '确认覆盖',
  'skills.notice.importFailed': '导入技能目录失败：{reason}',
  'skills.status.invalid': '无效',
  'skills.status.blocked': '已停用（两端）',
  'skills.status.shadowed': '被同名技能遮蔽',
  'skills.status.callable': '可调用：{audiences}',
  'skills.status.notCallable': '不可调用',
  'skills.row.toggles.aria': '{name} 的调用策略开关',
  'skills.row.modelToggle': '模型',
  'skills.row.userToggle': '用户',
  'skills.row.modelToggle.aria': '{name} 的模型调用（frontmatter 声明）',
  'skills.row.userToggle.aria': '{name} 的用户调用（frontmatter 声明）',
  'skills.row.modelToggle.hint': '改写该技能 SKILL.md 的 disable-model-invocation：关掉后模型看不到它、skill 工具也拒绝加载；正文与其余字段逐字保留',
  'skills.row.userToggle.hint': '改写该技能 SKILL.md 的 user-invocable：关掉后用户斜杠命令无法调用该技能；正文与其余字段逐字保留',
  'skills.status.audience.model': '模型',
  'skills.status.audience.user': '用户',
  'skills.row.status.aria': '技能状态：{status}',
  'skills.row.priority': '来源优先级 {rank}（仅在同一作用域内比较）',
  'skills.row.priorityShort': '优先级 {rank}',
  'skills.row.expand': '展开或收起技能 {name}',
  'skills.edit.content': '技能文件内容',
  'skills.edit.description': '描述',
  'skills.edit.hint': '这里只编辑 Markdown 正文，名称、描述和调用设置不属于正文。请先保存未保存的修改，再切换调用开关。',
  'skills.edit.save': '保存技能',
  'skills.edit.saving': '保存中…',
  'skills.edit.saved': '已同步',
  'skills.edit.dirty': '未保存',
  'skills.edit.loading': '读取中…',
  'skills.edit.unloaded': '尚未读取',
  'skills.edit.reload': '重新读取',
  'skills.edit.reloadConfirm': '重新读取将丢弃此技能的未保存修改，是否继续？',
  'skills.edit.readFailed': '技能读取失败，请重试',
  'skills.edit.saveFailed': '技能保存失败，修改已保留',
  'skills.row.noDescription': '（frontmatter 未提供描述）',
  'skills.row.shadowedHint': '当前会话实际使用另一个同名技能；下方开关仅修改此文件的调用策略。',
  'skills.row.delete': '删除',
  'skills.row.delete.hint': '将此技能的源文件或目录移入回收站，确认时显示准确路径',

  'presets.aria': '全局开关',
  'presets.writePreset.label': '全局开关',
  'presets.writePreset.hint': '控制所有已启用模块的运行贡献。关闭后停止后续注入并撤回工具贡献，模块定义与生成文件保留。',
  'moduleOrder.current': '返回当前模块',
  'moduleOrder.all': '跨模块排序',
  'moduleOrder.hint': '排列已启用模块中当前受众可见的配置；拖拽、上下移或使用拖动按钮的上下方向键，修改后自动保存。编辑内容请切回当前模块。',
  'moduleOrder.refresh': '刷新排序',
  'moduleOrder.discardRefresh': '放弃未保存排序并刷新',
  'moduleOrder.empty': '已启用模块中没有符合当前受众和筛选的配置卡。',
  'moduleOrder.disabled': '此配置卡已停用',
  'moduleOrder.unavailable': '排序服务不可用',
  'moduleOrder.readFailed': '读取排序失败：{reason}',
  'moduleOrder.saveFailed': '排序保存失败，调整已保留：{reason}',
  'moduleOrder.refreshFailed': '排序已保存，但当前模块读取失败，请刷新后再编辑。',

  'presetSwitcher.title': '模块模板',
  'presetSwitcher.hint': '管理模块及其自有资源。导入支持 ZIP、JSON、PNG、YAML 和文件夹，确认后创建或更新；每个模块可导出完整 ZIP 或仅定义 YAML。',
  'presetSwitcher.new': '新建模块',
  'presetSwitcher.import': '导入模块',
  'presetSwitcher.importing': '导入中…',
  'presetSwitcher.import.aria': '选择 module.yml / SillyTavern JSON 配置文件',
  'presetSwitcher.importDir': '导入文件夹',
  'presetSwitcher.importDir.aria': '选择模块文件夹',
  'presetSwitcher.export': '导出模块',
  'presetSwitcher.export.aria': '导出模块：{name}',
  'presetSwitcher.empty': '暂无模块；点击上方「新建模块」从内置模板创建，或「导入模块」添加。',
  'presetSwitcher.dialog.title': '从内置模板新建模块',
  'presetSwitcher.dialog.close': '关闭新建模块',
  'presetSwitcher.dialog.noTemplates': '插件目录无内置模板。',
  'presetSwitcher.custom.hint': '新建一份所有参数为空的自定义模块；重名时自动加序号',
  'presetSwitcher.custom.title': '自定义模块',
  'presetSwitcher.custom.detail': 'custom · 所有参数为空（空白起点，重名自动加序号）',
  'presetSwitcher.template.hint': '新建到用户目录：{id}',
  'presetSwitcher.card.blocked.hint': '模块不可渲染（缺模块清单/组合文件）：可删除后经「新建模块」从内置模板还原，或检查 module.yml',
  'presetSwitcher.card.enable.hint': '启用 {name}：参与运行时装配',
  'presetSwitcher.card.disable.hint': '停用 {name}：退出运行时装配',
  'presetSwitcher.blocked': '不可用',
  'presetSwitcher.duplicate.label': '复制模块',
  'presetSwitcher.duplicate.aria': '复制模块：{name}',
  'presetSwitcher.open.label': '打开模块文件夹',
  'presetSwitcher.open.aria': '打开模块文件夹：{name}',
  'presetSwitcher.delete.confirmHint': '删除后可从内置模板重新创建',
  'presetSwitcher.delete.confirm': '确认删除',
  'presetSwitcher.delete.cancel': '取消',
  'presetSwitcher.delete.hintActive': '先切换其他模块再删除',
  'presetSwitcher.delete.hint': '删除用户目录副本；内置模板仍可重新创建',
  'presetSwitcher.delete.aria': '删除模块：{name}',
  'presetSwitcher.notice.imported': '模块 {id} 已导入，打开卡片上的开关即可启用',
  'presetSwitcher.notice.importFailed': '导入模块失败：{reason}',
  'presetSwitcher.notice.exportFailed': '导出模块失败：{reason}',
  'presetSwitcher.notice.exported': '已导出 {file}（单文件配置，可保存到任意目录）',
  'presetSwitcher.notice.deleted': '模块 {id} 已删除（可经「新建模块」从内置模板还原）',
  'presetSwitcher.notice.deleteFailed': '删除模块失败：{reason}',
  'presetSwitcher.notice.duplicated': '已复制为模块 {id}',
  'presetSwitcher.notice.duplicateFailed': '复制模块失败：{reason}',
  'presetSwitcher.notice.opened': '已打开模块文件夹：{path}',
  'presetSwitcher.notice.openFailed': '打开模块文件夹失败：{reason}',
  'presetSwitcher.notice.cloned': '已从内置模板新建模块 {id}',
  'presetSwitcher.notice.cloneFailed': '新建模块失败：{reason}',
  'presetSwitcher.notice.enabled': '模块 {id} 已启用（参与运行时装配）',
  'presetSwitcher.notice.disabled': '模块 {id} 已停用（退出运行时装配）',
  'presetSwitcher.notice.enableFailed': '切换模块启用状态失败：{reason}',
} as const

/**
 * 字典键（zh 为唯一事实源；新增文案先加 zh，en 缺键即编译失败）。
 *
 * 文本量大的 feature 按归档计划 §8.3.1 分词典：核心词条留在本文件，其余按 feature
 * 放进 locales-params / locales-prompts / locales-cards，注册时合并为同一命名空间，
 * 不复制第二套翻译框架。
 */
export type PromptToolLocaleKey =
  | keyof typeof ZH_CORE
  | keyof typeof PARAMS_ZH
  | keyof typeof PROMPTS_ZH
  | keyof typeof CARDS_ZH
  | keyof typeof TRIGGERS_ZH

const EN_CORE: Record<keyof typeof ZH_CORE, string> = {
  'app.title': 'Prompt Tool',
  'app.backToChat': 'Back to chat',
  'app.loading': 'Loading',
  'app.statusSummary': '{configs} configs · {enabled} enabled',
  'app.open': 'Open prompt tool',
  'app.close': 'Close prompt tool',
  'app.panelAria': 'Prompt tool',
  'nav.aria': 'Prompt tool pages',
  'tab.label': 'Prompt Tool',

  'settings.aria': 'Prompt tool basic settings',
  'settings.saveRejected': 'Settings were not saved. Check your permissions or reload and retry.',
  'settings.writePreset.label': 'Enable prompt modules',
  'settings.writePreset.hint': 'Turning this off stops module injection and removes tool contributions. Definitions and generated files stay intact.',
  'settings.presetTemplate.title': 'Default module',
  'settings.presetTemplate.hint': 'Module mounted by default for new sessions; when the host switches a blank session to another module, the workbench follows it. Open the draggable floating button at the top-left for full module management and prompt configs.',
  'settings.switched': 'Switched to {id}; the current blank session was recomposed.',
  'settings.switchDefaultOnly': 'Default module updated to {id}; current session not switched: {reason}',
  'settings.switchPending': 'Default module updated to {id}; there is no blank session to recompose.',
  'settings.switchFailed': 'Failed to switch module: {reason}',
  'settings.switchReason.sessionNotBlank': 'The current session already has content and the host only switches modules on a blank session, so only the default for later sessions was updated',


  'page.features.label': 'Main session',
  'page.subagent.label': 'Subagent',
  'page.tools.label': 'Tool preview',
  'page.skills.label': 'Skills',
  'page.modules.label': 'Modules',

  'main.aria': 'Main session and global',
  'main.addTemplate': 'Add template · {layer}',
  'main.addToolTemplate': 'Add tool template…',
  'main.addVariables': 'Add template variables',
  'main.newBlankTool': 'New blank tool',
  'subagent.aria': 'Subagent',
  'configList.create': 'New',
  'configList.emptyPreStep': 'The current module template has no message-batch configs; you can create a custom config (written into the active module.yml, so it travels with that module).',

  'tools.aria': 'Tool preview',
  'tools.search.aria': 'Search tools',
  'tools.search.placeholder': 'Search tool name or description',
  'tools.surface.session': 'Current session tools',
  'tools.surface.preset': 'Official preset tool capabilities',
  'tools.surface.source.aria': 'Official preset source',
  'tools.surface.source.placeholder': 'Select an official preset',
  'tools.loadingPresets': 'Loading official presets…',
  'tools.loadFailed': 'Failed to load the official preset list',
  'tools.noPresets': 'No official presets available.',
  'tools.surface.sourceLabel': 'Tool surface',
  'tools.surface.count': 'Showing {visible} / {total} tools',
  'tools.surface.emptySource': 'No visible tools for this source.',
  'tools.surface.emptyFilter': 'No matching tools; adjust the search.',
  'tools.surface.list.aria': 'Model-visible tools',
  'tools.surface.card.aria': 'View tool {name}',
  'tools.surface.badge.visible': 'Model-visible',
  'tools.surface.detail.name': 'Full name',
  'tools.surface.detail.from': 'From',
  'tools.surface.detail.status': 'Visibility',
  'tools.surface.detail.description': 'Tool description',
  'tools.surface.detail.noDescription': '(no description)',
  'tools.surface.sub.count': '{count}',
  'tools.surface.noSession': 'No current session selected; sessions are never created or resumed automatically.',
  'tools.surface.noPreset': 'Select a module to read its tool capabilities.',
  'tools.surface.origin': 'Source: ',
  'tools.surface.refresh': 'Refresh tools',
  'tools.surface.loading': 'Loading tools…',
  'tools.surface.readFailed': 'Failed to read the tool surface',
  'tools.surface.retrySuffix': '; refresh to retry.',
  'tools.surface.note.session': 'Refreshing does not upgrade the generation of an existing session and never resumes it.',
  'tools.surface.note.preset': 'Module capabilities do not describe the current session; editing a module does not replace a frozen generation.',

  'characters.aria': 'Characters',
  'characters.importing': 'Importing…',
  'characters.badge.imported': 'Imported to current module',
  'characters.remove': 'Remove from current module',
  'characters.removing': 'Removing…',
  'characters.apply': 'Import to current module',
  'characters.openDir.label': 'Open character card folder',
  'characters.openDir.aria': 'Open character card folder: {name}',
  'characters.delete.label': 'Delete character card',
  'characters.delete.hintImported': 'Imported into the current module: remove it from the module first, then delete the card',
  'characters.delete.aria': 'Delete character card: {name}',
  'characters.confirmDelete': 'Confirm delete',
  'characters.cancel': 'Cancel',
  'characters.notice.applied': 'Imported to the current module ({count} configs)',
  'characters.notice.applyFailed': 'Failed to import into the current module: {reason}',
  'characters.notice.removed': 'Removed from the current module ({count} configs)',
  'characters.notice.removeFailed': 'Failed to remove: {reason}',
  'characters.notice.deleted': 'Character card {id} deleted; its entries in the current module were removed with it',
  'characters.notice.deleteFailed': 'Failed to delete: {reason}',
  'characters.notice.opened': 'Opened character card folder: {path}',
  'characters.notice.openFailed': 'Failed to open the folder: {reason}',

  'skills.aria': 'Skills',
  'skills.tabs.aria': 'Skill status filter',
  'skills.tabs.all': 'All',
  'skills.tabs.model': 'Model available',
  'skills.tabs.user': 'User available',
  'skills.tabs.blocked': 'Both disabled',
  'skills.incomplete': 'Skill discovery for this session is incomplete. Registration is unconfirmed; permitted file policy changes remain available.',
  'skills.refresh': 'Refresh',
  'skills.library.title': 'Skill directories and assets',
  'skills.library.meta': '{count} skills · from the official skill roots',
  'skills.library.hint': 'Skill entities stay in their own roots; this plugin never moves or links them — disabling only rewrites the invocation keys in that skill SKILL.md.',
  'skills.library.unknown': '(user skills directory unknown; reload the configuration)',
  'skills.library.open': 'Open user skills directory',
  'skills.library.footnote': 'Disabling rewrites the two official keys in that skill SKILL.md (disable-model-invocation / user-invocable): the model catalog, the skill tool and slash commands are toggled independently. Body, comments and every other field stay byte-identical, and writing false/true restores them. Read-only sources (for example packaged bundled roots) report the write failure instead of failing silently.',
  'skills.dir.rescan': 'Rescan',
  'skills.dir.cancel': 'Cancel',
  'skills.import.pick': 'Import skills',
  'skills.import.pick.hint': 'Choose a host directory and copy its contents into the user skills directory (copy, not reference)',
  'skills.folders.pick': 'Reference folder',
  'skills.folders.remove': 'Remove reference',
  'skills.folders.empty': 'No skill folder is referenced yet.',
  'skills.folders.duplicate': 'That folder is already referenced',
  'skills.folders.hint': 'References record paths without copying. Removing a reference leaves its files intact. Policy switches edit the original skill file; deleting a referenced skill moves its source to the recycle bin.',
  'skills.source.aria': 'Filter skills by source',
  'skills.source.all': 'All sources',
  'skills.source.project-dsh': 'Project .dsh/skills',
  'skills.source.project-agents': 'Project .agents/skills',
  'skills.source.custom': 'Skill folders',
  'skills.source.user-dsh': 'User skills directory',
  'skills.source.user-agents': 'User .agents/skills',
  'skills.source.bundled': 'Official bundled',
  'skills.source.other': 'Other providers',
  'skills.create.open': 'Create skill',
  'skills.create.close': 'Collapse the create form',
  'skills.create.name': 'Skill name (kebab-case)',
  'skills.create.namePlaceholder': 'for example my-skill',
  'skills.create.description': 'Description',
  'skills.create.descriptionPlaceholder': 'The model uses it to decide when the skill applies',
  'skills.create.content': 'Body (Markdown)',
  'skills.create.contentPlaceholder': '# Usage…',
  'skills.create.submit': 'Create',
  'skills.create.invalid': 'The skill name must be kebab-case and the description is required',
  'skills.create.hint': 'Creating writes into the user skills directory, where the official provider discovers it immediately.',
  'skills.delete.title': 'Delete skill {name}?',
  'skills.delete.description': 'Move the skill at this path to the recycle bin for manual recovery: {path}. Deleting a referenced skill moves its source files; removing only the folder reference leaves those files intact.',
  'skills.delete.confirm': 'Delete',
  'skills.delete.failed': 'Deletion failed. Review the error and retry.',
  'skills.filter.aria': 'Filter the skill list',
  'skills.filter.placeholder': 'Filter skills: name / folder / description…',
  'skills.group.meta': '{count} skills · priority {rank}',
  'skills.empty.title': 'No skills discovered',
  'skills.empty.hint': 'Expand directories and assets above, then choose "Import skills" to copy skills or "Reference folder" to use an existing directory. You can also create a new skill.',
  'skills.noMatch': 'No skills match the current filter.',
  'skills.notice.dirPickFailed': 'Failed to choose the directory: {reason}',
  'skills.notice.imported': 'Copied {count} skill files into {path}',
  'skills.notice.importedOverwrite': 'Copied {count} skill files into {path}, replacing {overwritten} confirmed skills',
  'skills.overwrite.title': 'Overwrite existing skills?',
  'skills.overwrite.description': 'These skill folders already exist: {names}. Confirm to replace their contents without keeping version history.',
  'skills.overwrite.confirm': 'Confirm overwrite',
  'skills.notice.importFailed': 'Failed to import the skills directory: {reason}',
  'skills.status.invalid': 'Invalid',
  'skills.status.blocked': 'Disabled (both ends)',
  'skills.status.shadowed': 'Shadowed by a same-name skill',
  'skills.status.callable': 'Callable: {audiences}',
  'skills.status.notCallable': 'Not callable',
  'skills.row.toggles.aria': 'Invocation policy switches for {name}',
  'skills.row.modelToggle': 'Model',
  'skills.row.userToggle': 'User',
  'skills.row.modelToggle.aria': 'Model invocation for {name} (frontmatter)',
  'skills.row.userToggle.aria': 'User invocation for {name} (frontmatter)',
  'skills.row.modelToggle.hint': 'Rewrites disable-model-invocation in this skill SKILL.md: when off the model cannot see the skill and the skill tool refuses to load it; body and every other field stay untouched',
  'skills.row.userToggle.hint': 'Rewrites user-invocable in this skill SKILL.md: when off the user slash command cannot invoke it; body and every other field stay untouched',
  'skills.status.audience.model': 'Model',
  'skills.status.audience.user': 'User',
  'skills.row.status.aria': 'Skill status: {status}',
  'skills.row.priority': 'Source priority {rank} (compared within the same scope)',
  'skills.row.priorityShort': 'Priority {rank}',
  'skills.row.expand': 'Expand or collapse skill {name}',
  'skills.edit.content': 'Skill file content',
  'skills.edit.description': 'Description',
  'skills.edit.hint': 'Edit only the Markdown body here; name, description and invocation settings are separate. Save pending edits before changing invocation switches.',
  'skills.edit.save': 'Save skill',
  'skills.edit.saving': 'Saving…',
  'skills.edit.saved': 'Synced',
  'skills.edit.dirty': 'Unsaved',
  'skills.edit.loading': 'Loading…',
  'skills.edit.unloaded': 'Not loaded',
  'skills.edit.reload': 'Reload',
  'skills.edit.reloadConfirm': 'Reloading discards unsaved changes to this skill. Continue?',
  'skills.edit.readFailed': 'Unable to read the skill. Try again.',
  'skills.edit.saveFailed': 'Unable to save the skill. Your edits are retained.',
  'skills.row.noDescription': '(frontmatter provides no description)',
  'skills.row.shadowedHint': 'This session uses another skill with the same name. These switches change only this file’s invocation policy.',
  'skills.row.delete': 'Delete',
  'skills.row.delete.hint': 'Move this skill’s source file or directory to the recycle bin; confirmation shows the exact path',

  'presets.aria': 'Global switches',
  'presets.writePreset.label': 'Global switch',
  'presets.writePreset.hint': 'Controls contributions from all enabled modules. Turning this off stops future injection and removes tool contributions while preserving definitions and generated files.',
  'moduleOrder.current': 'Back to current module',
  'moduleOrder.all': 'Order across modules',
  'moduleOrder.hint': 'Order enabled-module configurations for this audience. Drag, move up/down, or use the handle’s arrow keys; changes save automatically. Return to the current module to edit content.',
  'moduleOrder.refresh': 'Refresh order',
  'moduleOrder.discardRefresh': 'Discard unsaved order and refresh',
  'moduleOrder.empty': 'No enabled-module configurations match this audience and filters.',
  'moduleOrder.disabled': 'This card is disabled',
  'moduleOrder.unavailable': 'The ordering service is unavailable',
  'moduleOrder.readFailed': 'Unable to read order: {reason}',
  'moduleOrder.saveFailed': 'Unable to save order. Your changes are retained: {reason}',
  'moduleOrder.refreshFailed': 'Order saved, but the current module could not be reloaded. Refresh before editing.',

  'presetSwitcher.title': 'Module templates',
  'presetSwitcher.hint': 'Manage modules and their owned resources. Import ZIP, JSON, PNG, YAML or folders, then confirm creation or update. Export each module as a complete ZIP or definition YAML.',
  'presetSwitcher.new': 'New module',
  'presetSwitcher.import': 'Import module',
  'presetSwitcher.importing': 'Importing…',
  'presetSwitcher.import.aria': 'Choose a module.yml / SillyTavern JSON config file',
  'presetSwitcher.importDir': 'Import folder',
  'presetSwitcher.importDir.aria': 'Choose a module folder',
  'presetSwitcher.export': 'Export module',
  'presetSwitcher.export.aria': 'Export module: {name}',
  'presetSwitcher.empty': 'No modules yet; create one from a built-in template with "New module" above, or add one with "Import module".',
  'presetSwitcher.dialog.title': 'New module from a built-in template',
  'presetSwitcher.dialog.close': 'Close new module dialog',
  'presetSwitcher.dialog.noTemplates': 'The plugin directory has no built-in templates.',
  'presetSwitcher.custom.hint': 'Create a custom module with every parameter empty; duplicate names get a numeric suffix',
  'presetSwitcher.custom.title': 'Custom module',
  'presetSwitcher.custom.detail': 'custom · every parameter empty (blank start, duplicate names get a suffix)',
  'presetSwitcher.template.hint': 'Create in the user directory: {id}',
  'presetSwitcher.card.blocked.hint': 'Module cannot be rendered (missing module list/composition files): delete it and restore a built-in template, or inspect module.yml',
  'presetSwitcher.card.enable.hint': 'Enable {name}: take part in runtime assembly',
  'presetSwitcher.card.disable.hint': 'Disable {name}: leave runtime assembly',
  'presetSwitcher.blocked': 'Unavailable',
  'presetSwitcher.duplicate.label': 'Duplicate module',
  'presetSwitcher.duplicate.aria': 'Duplicate module: {name}',
  'presetSwitcher.open.label': 'Open module folder',
  'presetSwitcher.open.aria': 'Open module folder: {name}',
  'presetSwitcher.delete.confirmHint': 'You can recreate it from a built-in template afterwards',
  'presetSwitcher.delete.confirm': 'Confirm delete',
  'presetSwitcher.delete.cancel': 'Cancel',
  'presetSwitcher.delete.hintActive': 'Switch to another module before deleting this one',
  'presetSwitcher.delete.hint': 'Delete the user-directory copy; the built-in template can still recreate it',
  'presetSwitcher.delete.aria': 'Delete module: {name}',
  'presetSwitcher.notice.imported': 'Module {id} imported; turn on its card switch to enable it',
  'presetSwitcher.notice.importFailed': 'Failed to import the module: {reason}',
  'presetSwitcher.notice.exportFailed': 'Failed to export the module: {reason}',
  'presetSwitcher.notice.exported': 'Exported {file} (single-file config, save it anywhere)',
  'presetSwitcher.notice.deleted': 'Module {id} deleted (restore it from a built-in template with "New module")',
  'presetSwitcher.notice.deleteFailed': 'Failed to delete the module: {reason}',
  'presetSwitcher.notice.duplicated': 'Duplicated as module {id}',
  'presetSwitcher.notice.duplicateFailed': 'Failed to duplicate the module: {reason}',
  'presetSwitcher.notice.opened': 'Opened module folder: {path}',
  'presetSwitcher.notice.openFailed': 'Failed to open the module folder: {reason}',
  'presetSwitcher.notice.cloned': 'Created module {id} from a built-in template',
  'presetSwitcher.notice.cloneFailed': 'Failed to create the module: {reason}',
  'presetSwitcher.notice.enabled': 'Module {id} enabled (takes part in runtime assembly)',
  'presetSwitcher.notice.disabled': 'Module {id} disabled (leaves runtime assembly)',
  'presetSwitcher.notice.enableFailed': 'Failed to toggle the module enable state: {reason}',
}

const ZH: Record<PromptToolLocaleKey, string> = { ...ZH_CORE, ...PARAMS_ZH, ...PROMPTS_ZH, ...CARDS_ZH, ...TRIGGERS_ZH }
const EN: Record<PromptToolLocaleKey, string> = { ...EN_CORE, ...PARAMS_EN, ...PROMPTS_EN, ...CARDS_EN, ...TRIGGERS_EN }

/** 官方 locale 服务的最小使用面（便于 node:test 用 mock 覆盖注册/释放）。 */
export interface PromptToolLocaleRegistry {
  register(
    ns: typeof PROMPT_TOOL_NS,
    dicts: { zh: Record<PromptToolLocaleKey, string>; en: Record<PromptToolLocaleKey, string> },
  ): () => void
}

/** 命名空间翻译函数（调用时读当前语言）。 */
export type PromptToolTranslate = Translate<PromptToolLocaleKey>

declare module '@deepseek-ai/dsh-client-ui-slots' {
  interface LocaleNamespaceMap {
    'prompt-tool': PromptToolLocaleKey
  }
}

export const PROMPT_TOOL_DICTS: { zh: Record<PromptToolLocaleKey, string>; en: Record<PromptToolLocaleKey, string> } = {
  zh: ZH,
  en: EN,
}

/** 注册 zh/en 字典；返回官方 locale 服务的幂等 disposer。 */
export function registerPromptToolLocale(locale: PromptToolLocaleRegistry): () => void {
  return locale.register(PROMPT_TOOL_NS, PROMPT_TOOL_DICTS)
}
